/**
 * CrmProposalDetailModal — read view of a proposal's computed fee breakdown (FPV, base/weighted %,
 * per-phase fees + hours, package totals or retainer), status, and any posted/signed PDFs.
 * "Edit" opens CrmProposalEditor.jsx (the authoring form); "Preview PDF" / "Preview web view"
 * (Basic Services + Design Consultation — see HAS_WEB_VIEW) render the same document the editor's
 * footer buttons do.
 */
import { useState, useEffect } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import CrmProposalEditor from './CrmProposalEditor.jsx'
import UploadSignedAgreementModal from './UploadSignedAgreementModal.jsx'
import { previewProposalPdf, previewProposalHtml, freezeProposalPdf, markPosted, resetProposal, getCountersignStatus, countersignProposal, updateProposal, extendProposalExpiration, proposalPhaseBreakdown, HAS_WEB_VIEW, proposalDisplayTitle } from '../../services/proposals.js'
import { getOrCreateLink, upsertEntry, setStatus, listEntries, deleteEntry } from '../../services/journey.js'
import { advanceToProposalPosted } from '../../services/opportunities.js'
import { logInteraction } from '../../services/interactions.js'
import { ctx } from '../../outline/ctx.js'
import { getFeeOverride, includedCodesFor, overrideDrift, phaseLabel } from '../../services/proposalFeeOverride.js'
import { alertDialog, confirmDialog, promptDialog } from '../Dialog.jsx'

async function openPreview(id) {
  const tab = window.open('', '_blank')
  try { const url = await previewProposalPdf(id, { working: true }); if (tab) tab.location = url; else window.open(url, '_blank') }
  catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: (e && e.message) || 'Could not generate the PDF.' }) }
}

async function openPreviewWeb(id) {
  const tab = window.open('', '_blank')
  try { const url = await previewProposalHtml(id, { working: true }); if (tab) tab.location = url; else window.open(url, '_blank') }
  catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: (e && e.message) || 'Could not generate the web view.' }) }
}

const STATUS_COLOR = { Draft: '#8a8f98', Posted: '#7c3aed', Executed: '#1f9d57', Declined: '#b3261e', Expired: '#9a6700', Cancelled: '#8a8f98', Replaced: '#8a8f98', 'Pending countersignature': '#b8540a' }
const PACKAGE_LABEL = { A: 'Essential Design', B: 'Premier Architecture', C: 'Architecture + Interiors' }

function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '—' }
function pct(n) { return n == null ? '—' : (Math.round(n * 1000) / 10) + '%' }
function fmtDate(v) { if (!v) return '—'; try { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return String(v) } }

export default function CrmProposalDetailModal({ proposal: p, onClose, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [uploadingSigned, setUploadingSigned] = useState(false)
  const [busy, setBusy] = useState(false)
  const [cs, setCs] = useState(null)   // countersign status — see api/proposal-countersign.js
  // All-inclusive fixed fee, when this proposal has one. Absent for the overwhelming majority.
  const [feeOverride, setFeeOverride] = useState(null)
  const debug = typeof document !== 'undefined' && document.body.classList.contains('aj-debug')

  useEffect(() => {
    let cancelled = false
    getFeeOverride(p.id).then(ov => { if (!cancelled) setFeeOverride(ov) }).catch(() => {})
    return () => { cancelled = true }
  }, [p.id])

  useEffect(() => {
    // Fetch on `posted` (not `accepted`) — a joint proposal can sit with one signer done and
    // one still pending, which never flips Airtable's Accepted field, but is exactly the state
    // this status line + the per-signer breakdown need to be visible for.
    if (!p.posted) { setCs(null); return }
    let cancelled = false
    getCountersignStatus(p.id).then(s => { if (!cancelled) setCs(s) }).catch(() => {})
    return () => { cancelled = true }
  }, [p.id, p.posted])

  if (editing) return <CrmProposalEditor proposal={p} onClose={() => setEditing(false)} onSaved={() => { if (onChanged) onChanged() }} />

  // Replace: void this posted-but-not-executed proposal (Status -> 'Replaced', a real Airtable
  // choice — no schema change needed) and open a fresh draft pre-filled with its details, so a
  // client's requested changes can be processed without touching the original record. The old
  // record, its posted/signed PDFs, and its Airtable history are left completely intact — only its
  // Status changes and its client-journey card is pulled so it can't be viewed/signed anymore.
  if (replacing) {
    return (
      <CrmProposalEditor
        opportunityId={p.opportunityId} opportunityName={p.projectName} initialType={p.type} seedFrom={p}
        onClose={() => { setReplacing(false); onClose() }}
        onSaved={() => { if (onChanged) onChanged(); onClose() }}
      />
    )
  }
  const isDC = p.type === 'Design Consultation'

  async function doCountersign() {
    const ok = await confirmDialog({
      title: 'Countersign this proposal?',
      message: 'This applies your saved signature (Settings > Personal Signature) to finalize the agreement — a real second signing action, not automatic. The client receives the fully executed copy if they opted into email updates. This cannot be undone.',
      confirmLabel: 'Countersign',
    })
    if (!ok) return
    setBusy(true)
    try {
      await countersignProposal(p.id)
      setCs(await getCountersignStatus(p.id))
      await alertDialog({ title: 'Countersigned', message: 'The agreement is now fully executed.' })
      if (onChanged) onChanged()
    } catch (e) {
      await alertDialog({ title: 'Countersign failed', message: e.message })
    } finally { setBusy(false) }
  }

  async function replaceProposal() {
    if (!(await confirmDialog({
      title: 'Replace this proposal?',
      message: `${p.name || 'This proposal'} will be marked Replaced and pulled from the Client Hub — it can no longer be viewed or signed. A new draft opens next, pre-filled with the same details, ready for the requested changes. The original record and its history are untouched.`,
      confirmLabel: 'Replace',
    }))) return
    setBusy(true)
    try {
      await updateProposal(p.id, { Status: 'Replaced' })
      // Pull the card from the Client Hub so the old terms can't be viewed or signed anymore —
      // the Fee Proposal record itself (and its posted/signed PDFs) are left completely alone.
      try {
        const { journey } = await getOrCreateLink(p.opportunityId, p.projectName)
        if (journey && journey.id) {
          const { entries } = await listEntries(journey.id)
          const stale = (entries || []).filter(e => e.type === 'proposal' && e.data && e.data.proposalId === p.id)
          for (const e of stale) await deleteEntry(e.id).catch(() => {})
        }
      } catch (_) { /* best-effort */ }
      logInteraction({
        type: 'Action Taken', projectId: p.opportunityId,
        notes: `${p.name || 'Proposal'} was replaced at the client's request. A new draft has been started with the same details.`,
      }).catch(() => {})
      if (onChanged) onChanged()
      setReplacing(true)
    } catch (e) {
      await alertDialog({ title: 'Replace failed', message: e.message })
    } finally { setBusy(false) }
  }

  async function extendExpiration() {
    const input = await promptDialog({
      title: 'Extend expiration',
      message: `Currently set to expire ${fmtDate(p.expirationDate)}. Add how many days?`,
      placeholder: 'e.g. 14',
    })
    if (input === null) return
    const days = parseInt(input, 10)
    if (!Number.isFinite(days) || days <= 0) {
      await alertDialog({ title: 'Invalid number', message: 'Enter a whole number of days greater than zero.' })
      return
    }
    setBusy(true)
    try {
      const updated = await extendProposalExpiration(p.id, p.expirationTimeline, days)
      await alertDialog({ title: 'Extended', message: `New expiration date: ${fmtDate(updated?.expirationDate)}.` })
      if (onChanged) onChanged()
    } catch (e) {
      await alertDialog({ title: 'Could not extend', message: e.message })
    } finally { setBusy(false) }
  }

  async function resetTest() {
    if (!(await confirmDialog({
      title: 'Unpost proposal (test)?',
      message: 'Clears Posted/Accepted on this proposal and removes its card from the Client Hub, so the full post -> sign -> executed cycle can be re-tested from scratch. Does not affect other hub entries.',
      confirmLabel: 'Unpost', danger: true,
    }))) return
    setBusy(true)
    try { await resetProposal(p.id); if (onChanged) onChanged(); await alertDialog({ title: 'Reset', message: 'The proposal is unposted and its hub card removed.' }); onClose() }
    catch (e) { await alertDialog({ title: 'Reset failed', message: e.message }) }
    finally { setBusy(false) }
  }

  async function postToJourney() {
    const ok = await confirmDialog({
      title: 'Post & send to client?',
      message: 'This freezes the issued PDF, marks the proposal posted, and publishes it to the Client Hub for review and signature. This is outward-facing.',
      confirmLabel: 'Post & send',
    })
    if (!ok) return
    setBusy(true)
    try {
      const frozen = await freezeProposalPdf(p.id)
      await markPosted(p.id, { memberId: ctx.currentMember && ctx.currentMember.id })
      // Native replacements for the retired Post-time Airtable automation (Contract Stage advance
      // + the 'Proposal/Contract Posted' interaction log). Best-effort — never block the post.
      const posterName = (ctx.currentMember && ctx.currentMember.fields &&
        (ctx.currentMember.fields['Full Name'] || ctx.currentMember.fields['Name'] || ctx.currentMember.fields['Member Name'])) || 'a team member'
      advanceToProposalPosted(p.opportunityId).catch(() => {})
      logInteraction({
        type: 'Action Taken', opportunityType: 'Proposal/Contract Posted', projectId: p.opportunityId,
        notes: `The proposal was posted by ${posterName}. Editing has been disabled.`,
      }).catch(() => {})
      const { journey } = await getOrCreateLink(p.opportunityId, p.projectName)
      await upsertEntry({
        journeyId: journey.id, type: 'proposal', title: proposalDisplayTitle(p.type), published: true,
        data: { proposalId: p.id, proposalType: p.type, contractFee: p.contractFee, retainer: p.retainer, tiered: p.tiered, packages: p.packages, pdfUrl: frozen.url, proposalDate: p.proposalDate },
      })
      try { await setStatus(journey.id, 'active') } catch (_) {}
      await alertDialog({ title: 'Posted', message: 'The proposal is live in the Client Hub for review and signature.' })
      if (onChanged) onChanged()
      onClose()
    } catch (e) {
      await alertDialog({ title: 'Post failed', message: (e && e.message) || 'Could not post the proposal.' })
    } finally { setBusy(false) }
  }
  const isASR = p.type === 'ASR Proposal'
  // Suite packages come from Airtable's engine. An all-inclusive fee has replaced that with one
  // negotiated number, and the document prints a single fee column — showing three package prices
  // beside it would quote the client figures their proposal does not contain.
  const hasPackages = !isDC && !isASR && !feeOverride && (p.packages.A || p.packages.B || p.packages.C)
  // The phases exactly as the client's own document presents them — inclusion from
  // `Phases Included`, Permitting and the two trailing phases always hourly, the FRIENDS/HOURLY
  // fee presentations respected. Filtering on `fee > 0` (what this used to do) answered a
  // different question and got both directions wrong: an included phase priced at 0 disappeared,
  // and Permitting showed a fixed fee the proposal itself never quotes.
  const breakdown = !isDC && !isASR ? proposalPhaseBreakdown(p, feeOverride) : null
  // Does the saved split still match the phases this proposal sells? While it does not, the
  // document refuses to render — say so here rather than letting someone discover it by
  // clicking Preview.
  const feeDrift = feeOverride ? overrideDrift(feeOverride, includedCodesFor(p.raw)) : { ok: true, missing: [], extra: [] }

  // Once the client has signed, Airtable's own Display Status doesn't know about the countersign
  // step yet — override the badge from the more precise Supabase state we just fetched.
  const displayStatus = cs?.countersignedAt ? 'Executed' : cs?.pending ? 'Pending countersignature' : p.status
  const displayColor = STATUS_COLOR[displayStatus] || '#5a7884'
  const canReplace = !isASR && p.posted && !['Executed', 'Replaced', 'Cancelled'].includes(displayStatus)
  // A wet-signed proposal may never have gone through "Post & send to client" at all — unlike
  // Replace, this isn't restricted to posted proposals.
  const canRecordManualExecution = !isASR && !['Executed', 'Replaced', 'Cancelled'].includes(displayStatus)
  // The alternative to Replace when the client still wants to sign the SAME terms but the
  // proposal lapsed before they got to it — Replace starts a fresh draft, this just pushes the
  // existing one's clock forward.
  const canExtend = displayStatus === 'Expired'

  return (
    <>
    <CrmModal title={p.name || p.projectName || 'Proposal'} onClose={onClose} width={640}
      footer={<>
        <button style={F.btnGhost} onClick={onClose} disabled={busy}>Close</button>
        {HAS_WEB_VIEW.has(p.type) && <button style={F.btnGhost} onClick={() => openPreviewWeb(p.id)} disabled={busy}>Preview web view</button>}
        <button style={F.btnGhost} onClick={() => openPreview(p.id)} disabled={busy}>Preview PDF</button>
        {!p.posted && <button style={F.btnGhost} onClick={() => setEditing(true)} disabled={busy}>Edit</button>}
        {canExtend && <button style={F.btnGhost} onClick={extendExpiration} disabled={busy}>Extend…</button>}
        {canReplace && <button style={{ ...F.btnGhost, color: '#b3261e' }} onClick={replaceProposal} disabled={busy}>Replace…</button>}
        {canRecordManualExecution && <button style={F.btnGhost} onClick={() => setUploadingSigned(true)} disabled={busy}>Signed outside the app…</button>}
        {p.posted && debug && <button style={{ ...F.btnGhost, color: '#b8540a' }} onClick={resetTest} disabled={busy} title="Test only — visible in /debug mode">↩ Unpost (test)</button>}
        {!p.posted && !p.accepted && <button style={F.btnPrimary} onClick={postToJourney} disabled={busy}>{busy ? 'Posting…' : 'Post & send to client'}</button>}
        {cs?.pending && <button style={F.btnPrimary} onClick={doCountersign} disabled={busy}>{busy ? 'Countersigning…' : 'Countersign'}</button>}
      </>}>
      <div style={S.metaRow}>
        <span style={{ ...S.badge, background: displayColor + '1a', color: displayColor }}>{displayStatus}</span>
        <span style={S.type}>{p.type}</span>
        {p.selectedPackage && <span style={S.selPkg}>Selected: {p.selectedPackage}</span>}
      </div>
      {cs?.signers && cs.signers.length > 1 && (
        <div style={S.signersList}>
          {cs.signers.map((s, i) => (
            <div key={i} style={{ ...S.signerRow, ...(i === cs.signers.length - 1 ? { borderBottom: 'none' } : {}) }}>
              <span style={{ ...S.signerDot, background: s.signed ? '#1f9d57' : 'var(--aj-text-mid)' }} />
              <span style={S.signerName}>{s.name}</span>
              <span style={s.signed ? S.signerStatusDone : S.signerStatusPending}>{s.signed ? `Signed ${fmtDate(s.signedAt)}` : 'Pending'}</span>
            </div>
          ))}
        </div>
      )}
      {cs?.pending && (
        <div style={S.pendingNote}>
          Signed by {cs.clientSignerName || 'the client'} on {fmtDate(cs.clientSignedAt)}. Awaiting Alder James's countersignature to become fully executed.
        </div>
      )}
      {!cs?.pending && !cs?.countersignedAt && cs?.signers && cs.signers.length > 1 && cs.signers.some(s => s.signed) && (
        <div style={S.pendingNote}>
          Countersignature isn't offered yet — waiting on the remaining signer(s) above before the client side counts as complete.
        </div>
      )}
      {cs?.countersignedAt && (
        <div style={S.pendingNote}>
          Countersigned by {cs.countersignedByName || 'Alder James'} on {fmtDate(cs.countersignedAt)}.
        </div>
      )}

      <div style={S.fieldGrid}>
        <Field label="Client" value={p.clientName} />
        <Field label="Project" value={p.projectName} />
        <Field label="Project type" value={p.typeOfProject} />
        <Field label="Proposal date" value={fmtDate(p.proposalDate)} mono />
        <Field label="Expiration" value={fmtDate(p.expirationDate)} mono />
        <Field label="Contract fee" value={money(p.contractFee)} mono />
      </div>

      {/* Fee math */}
      <div style={S.sectionHead}>Fee</div>
      <div style={S.fieldGrid}>
        <Field label="Factored project value" value={money(p.fpv)} mono />
        <Field label="Base fee %" value={pct(p.baseFeePct)} mono />
        <Field label="Weighted fee %" value={pct(p.weightedFeePct)} mono />
        <Field label="Weighted fee amount" value={money(p.weightedFeeAmount)} mono />
        {(isDC || p.retainer > 0) && <Field label="Retainer" value={money(p.retainer)} mono />}
        {isDC && <Field label="Estimated BS value" value={money(p.estimatedBsFpv)} mono />}
        <Field label="Total budget hours" value={p.totalHours ? Math.round(p.totalHours).toLocaleString() : '—'} mono />
      </div>

      {/* Packages (Basic Services tiered) */}
      {hasPackages && (
        <>
          <div style={S.sectionHead}>Packages</div>
          <div style={S.pkgRow}>
            {['A', 'B', 'C'].map(k => (
              <div key={k} style={S.pkg}>
                <div style={S.pkgName}>{PACKAGE_LABEL[k]}</div>
                <div style={S.pkgFee}>{money(p.packages[k])}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Phase breakdown — mirrors the document (see proposalPhaseBreakdown) */}
      {breakdown && (
        <>
          <div style={S.sectionHead}>Phase breakdown</div>
          {feeOverride && (
            <div style={S.aiBanner}>
              <strong>All-inclusive fixed fee — {money(feeOverride.totalFee)}.</strong>{' '}
              Quoted manually and split across the phases below, including Permitting, Bidding
              &amp; Negotiation and Construction Support, which this firm normally bills hourly.
              The fee engine's own numbers are not used on this proposal.
              {feeOverride.note ? <div style={S.aiNote}>{feeOverride.note}</div> : null}
              {!feeDrift.ok && (
                <div style={S.aiDrift}>
                  This split no longer matches the phases the proposal sells
                  {feeDrift.missing.length ? ` (sold but not priced: ${feeDrift.missing.map(phaseLabel).join(', ')})` : ''}
                  {feeDrift.extra.length ? ` (priced but not sold: ${feeDrift.extra.map(phaseLabel).join(', ')})` : ''}
                  . The document will not render until it is re-split — open Edit and use the
                  all-inclusive fee panel.
                </div>
              )}
            </div>
          )}
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Phase</th><th style={S.thN}>Fee</th>
              <th style={S.thN}>Hours</th><th style={S.thN}>Adjustment</th>
            </tr></thead>
            <tbody>
              {breakdown.rows.map(ph => (
                <tr key={ph.code}>
                  <td style={ph.included ? S.td : S.tdOut}>
                    {ph.label}
                    {ph.suiteTag && breakdown.tiered && ph.included && <span style={S.suiteTag}>{ph.suiteTag}</span>}
                  </td>
                  <td style={ph.included ? S.tdN : S.tdNOut}><PhaseFee ph={ph} mode={breakdown.feeMode} /></td>
                  <td style={ph.included ? S.tdN : S.tdNOut}>{ph.included && ph.hours ? Math.round(ph.hours) : '—'}</td>
                  <td style={ph.included ? S.tdN : S.tdNOut}>
                    {!ph.included || ph.adjustment == null ? '—'
                      : <span style={ph.adjustment === 1 ? undefined : S.adjOn}>{Math.round(ph.adjustment * 1000) / 10}%</span>}
                  </td>
                </tr>
              ))}
              {/* The document prints no fixed total in HOURLY mode, and for a TIERED proposal its
                  totals row is the three package columns -- which the Packages section above
                  already shows -- so neither case gets a single-figure total here. */}
              {breakdown.feeMode !== 'HOURLY' && !breakdown.tiered && breakdown.total > 0 && (
                <tr>
                  <td style={S.tdTotal}>{breakdown.totalLabel}</td>
                  <td style={S.tdNTotal}>
                    {breakdown.feeMode === 'FRIENDS' && breakdown.totalNoFF > 0
                      ? <><span style={S.strike}>{money(breakdown.totalNoFF)}</span> {money(breakdown.total)}</>
                      : money(breakdown.total)}
                  </td>
                  <td style={S.tdNTotal} /><td style={S.tdNTotal} />
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {(p.postedPdf || p.signedAgreement || cs?.clientSignedPdfUrl) && (
        <div style={S.docs}>
          {p.postedPdf && <a href={p.postedPdf} target="_blank" rel="noopener" style={S.docLink}>Posted proposal PDF ↗</a>}
          {!p.signedAgreement && cs?.clientSignedPdfUrl && <a href={cs.clientSignedPdfUrl} target="_blank" rel="noopener" style={S.docLink}>Client-signed copy (pending countersignature) ↗</a>}
          {p.signedAgreement && <a href={p.signedAgreement} target="_blank" rel="noopener" style={S.docLink}>Signed agreement ↗</a>}
        </div>
      )}

    </CrmModal>
    {uploadingSigned && (
      <UploadSignedAgreementModal proposal={p} onClose={() => setUploadingSigned(false)}
        onDone={() => { if (onChanged) onChanged() }} />
    )}
    </>
  )
}

/** The fee cell, in the document's own three presentations. Hourly wins over everything: a phase
    billed hourly has no fixed fee to strike through or discount. */
function PhaseFee({ ph, mode }) {
  if (!ph.included) return <span style={S.hourlyCell}>Not included</span>
  if (ph.hourly) return <span style={S.hourlyCell}>Hourly</span>
  if (mode === 'FRIENDS' && ph.feeNoFF > 0 && ph.feeNoFF !== ph.fee) {
    return <><span style={S.strike}>{money(ph.feeNoFF)}</span> {money(ph.fee)}</>
  }
  return money(ph.fee)
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div style={F.label}>{label}</div>
      <div style={{ fontFamily: mono ? '"DM Mono", monospace' : '"DM Sans", sans-serif', fontSize: 13, color: value && value !== '—' ? 'var(--aj-text)' : 'var(--aj-text-mid)' }}>{value || '—'}</div>
    </div>
  )
}

const S = {
  metaRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  badge: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px' },
  pendingNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginTop: -6, marginBottom: 14, lineHeight: 1.5 },
  signersList: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: -6, marginBottom: 10, border: '0.5px solid var(--aj-border)', borderRadius: 10, overflow: 'hidden' },
  signerRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--aj-bg-subtle)', borderBottom: '0.5px solid var(--aj-border)' },
  signerDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  signerName: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', flex: 1 },
  signerStatusDone: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#1f9d57' },
  signerStatusPending: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)' },
  type: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)' },
  selPkg: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#1f9d57', marginLeft: 'auto' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 6 },
  sectionHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', margin: '16px 0 8px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  aiBanner: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.5, color: 'var(--aj-text)', background: 'rgba(29,158,117,0.07)', border: '0.5px solid rgba(29,158,117,0.35)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 },
  aiDrift: { marginTop: 6, color: '#b3261e', fontWeight: 600 },
  aiNote: { marginTop: 4, color: 'var(--aj-text-muted)', fontStyle: 'italic' },
  pkgRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 },
  pkg: { background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '10px 12px' },
  pkgName: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)' },
  // fontWeight 500 (not 600) + explicit antialiasing: bold light-colored mono text on the dark
  // subtle-bg card halates ("glows") under default font smoothing — this is the standard fix.
  pkgFee: { fontFamily: '"DM Mono", monospace', fontSize: 15, fontWeight: 500, color: 'var(--aj-text)', marginTop: 4, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
  table: { width: '100%', borderCollapse: 'collapse' },
  // Excluded phases: same row, visibly not part of the proposal.
  tdOut: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)', padding: '5px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  tdNOut: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-muted)', padding: '5px 6px', textAlign: 'right', borderBottom: '0.5px solid var(--aj-border)' },
  tdTotal: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', padding: '7px 6px' },
  tdNTotal: { fontFamily: '"DM Mono", monospace', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', padding: '7px 6px', textAlign: 'right' },
  suiteTag: { fontFamily: '"DM Mono", monospace', fontSize: 9.5, letterSpacing: '0.04em', color: '#1a6fa8', background: 'rgba(26,111,168,0.1)', borderRadius: 3, padding: '1px 5px', marginLeft: 7, whiteSpace: 'nowrap' },
  hourlyCell: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontStyle: 'italic', color: 'var(--aj-text-mid)' },
  strike: { textDecoration: 'line-through', color: 'var(--aj-text-muted)' },
  // An adjustment that is not 100% is the whole reason to show the column — make it findable.
  adjOn: { color: '#b8540a', fontWeight: 600 },
  th: { textAlign: 'left', fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  thN: { textAlign: 'right', fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  td: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', padding: '5px 6px', borderBottom: '0.5px solid var(--aj-border)' },
  tdN: { fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', padding: '5px 6px', textAlign: 'right', borderBottom: '0.5px solid var(--aj-border)' },
  docs: { display: 'flex', gap: 16, marginTop: 14 },
  docLink: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#1a6fa8', textDecoration: 'none' },
}
